import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_chevron_left } from '@repo/icons/index'
import classNames from 'classnames'
import { useState } from 'react'
import 'react-reflex/styles.css'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsAdminLogin from '../../../../modules/hooks/common/useIsAdminLogin'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useWebviewNavigation from '../../../../modules/hooks/webview/common/useWebviewNavigation'
import { LOCALE_EN, LOCALE_KO } from '../../../../modules/i18n/config'
import { n_common, n_lesson_prep } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import { preventClose } from '../../../../pages/[lang]/portal/lessons/preparation/[lessonId]'
import { useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import LoadingSpinner from '../../../common/LoadingSpinner'
import CreditBadge from '../../../common/buttons/CreditBadge'
import LessonSettingConfirmPopup from '../../../student/new-portal/lessons/LessonSettingConfirmPopup'
import GoogleTranslateBtn from '../../GoogleTranslateBtn'
import { handleSubmitAnswer } from '../../materials/QuestionSelect/QuestionSelectPanel'
import CompletePreparationTooltip from './CompletePreparationTooltip'
import PreparationAnswerNotSaved from './PreparationAnswerNotSaved'
import PreparationNotYet from './PreparationNotYet'

interface PreparationTabProps {
  lesson: any
  isEditable: any
  setIsEditable: any
  originalLesson: any
  showGoogleTranslator?: any
  setShowGoogleTranslator?: any
  showTooltip: any
  setShowTooltip: any
  isReview?: any
}

const PreparationTab = ({
  lesson,
  isEditable,
  setIsEditable,
  originalLesson,
  showGoogleTranslator,
  setShowGoogleTranslator,
  showTooltip,
  setShowTooltip,
  isReview = false,
}: PreparationTabProps) => {
  const router = useAppRouter()
  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()
  const appRouter = useAppRouter({ stackPush: push })
  const { sendClickEvent } = useEventTracking()
  const isFreeTalkingOrWriting = lesson?.course?.is_writing || lesson?.course?.free_talking
  const disabled = isFreeTalkingOrWriting || lesson?.prestudy?.rate === 100 ? false : true // 수업 준비가 다 안끝난 경우 true
  const originalAnswers = originalLesson?.coursekeyquestions_reordering
  const { t, locale, isLocaleKO } = useTranslation()
  const isAdminLogin = useIsAdminLogin()
  const { currentUser } = useCurrentUser()
  const isWebview = useIsWebview()
  const { popBackToRingleApp } = useWebviewNavigation()
  const { sendPageViewEvent } = useEventTracking()

  let count = 0
  for (const item of isEditable) {
    const originalAnswer = originalAnswers?.find((originalItem) => originalItem.question_number == item.question_number)

    if (
      originalAnswer &&
      item.editable &&
      (item.student_answer?.content ||
        (originalAnswer.student_answer?.content && item.student_answer?.content?.length === 0))
    ) {
      count++
    }
  }

  const notSaved = count > 0 ? true : false

  const [showPrepPopup, setShowPrepPopup] = useState(false)
  const [showSavePopup, setShowSavePopup] = useState(false)
  const [showSaveBeforeOverPopup, setShowSaveBeforeOverPopup] = useState(false)
  const [showSettingPopup, setShowSettingPopup] = useState(false)
  const [lessonSettingData, setLessonSettingData] = useState(null)
  const [info, setInfo] = useState(null)
  const [loadSettingInfo, setLoadingSettingInfo] = useState(false)

  const questionCnt = originalLesson?.duration == 20 ? 2 : 3
  const tooltipContent = t(n_lesson_prep.overview.tooltip_done(questionCnt))

  //notSaved => custom popup 으로 케이스 처리

  const onClickButton = () => {
    if (notSaved && count > 0) {
      setShowSaveBeforeOverPopup(true)
    } else {
      router.push(urls.student.newPortal.lessons.preparationOver.as(lesson?.id))
    }
  }

  const fetchSetting = async () => {
    const response = await axiosGet(apis.lessons.setting(lesson.id))

    if (response) {
      setLessonSettingData(response.response)
    }
  }

  const fetchLesson = async () => {
    const response = await axiosGet(apis.lessons.classroom({ lessonId: lesson.id }))
    if (response) {
      console.log(response)

      setInfo(response.lesson)
    }
  }

  const onClickLessonSetting = async () => {
    sendClickEvent({ button_name: 'lesson_info' })
    setLoadingSettingInfo(true)

    try {
      fetchSetting()
      fetchLesson()
      setShowSettingPopup(true)

      setLoadingSettingInfo(false)
    } catch {
      console.log('error')
    }
  }

  const handlePrepPopupClose = () => {
    setShowPrepPopup(false)
  }

  // 수업 준비 완료가 아닌 상태에서 이탈하려고 할 때
  const handlePrepExit = () => {
    if (notSaved && count > 0) {
      setShowSavePopup(true)
    } else if (disabled) {
      setShowPrepPopup(true)
    }
  }

  const lessonId = router.query?.lessonId
  const handleSaveButton = async () => {
    try {
      const apiCalls = []

      for (const item of isEditable) {
        if (item.editable && item.student_answer && item.student_answer?.content != '') {
          const apiCall = handleSubmitAnswer({
            id: item.id,
            content: item.student_answer?.content,
            shareStatus: item?.student_answer?.share_status || 0,
            isEditable,
            setIsEditable,
            activeQuestionId: item.question_number,
            lessonId,
          })
          apiCalls.push(apiCall)
        }
      }

      // 모든 API 호출을 기다림
      await Promise.all(apiCalls)

      window.removeEventListener('beforeunload', (e) => preventClose(e, t(n_lesson_prep.popup_leave_default)))

      if (disabled) {
        // 다 저장은 했지만 수업 준비가 완료되지 않은 케이스 처리
        setShowSavePopup(false)
        setShowPrepPopup(true)
      } else {
        setShowSavePopup(false)
        // 이전 페이지로 이동
        if (isWebview) {
          popBackToRingleApp()
        } else {
          // router.push(urls.student.newPortal.lessons.upcomingLessons)
          router.back()
        }
      }
    } catch (error) {
      // 오류 처리
      console.error(error)
    }
  }

  const handleSaveBeforeOverButton = async () => {
    try {
      const apiCalls = []

      for (const item of isEditable) {
        if (item.editable && item.student_answer && item.student_answer?.content != '') {
          const apiCall = handleSubmitAnswer({
            id: item.id,
            content: item.student_answer?.content,
            shareStatus: item?.student_answer?.share_status || 0,
            isEditable,
            setIsEditable,
            activeQuestionId: item.question_number,
            lessonId,
          })
          apiCalls.push(apiCall)
        }
      }

      // 모든 API 호출을 기다림
      await Promise.all(apiCalls)

      window.removeEventListener('beforeunload', (e) => preventClose(e, t(n_lesson_prep.popup_leave_default)))

      setShowSaveBeforeOverPopup(false)
      if (isWebview) {
        appRouter.push(urls.webview.lessons.preparation_over(lesson?.id), {
          stackActivity: 'LessonPreparationOverActivity',
          stackSendData: {
            lessonId: lesson?.id,
          },
        })
      } else {
        appRouter.push(urls.student.newPortal.lessons.preparationOver.as(lesson?.id))
      }
    } catch (error) {
      // 오류 처리
      console.error(error)
    }
  }

  const goToReview = () => {
    router.back()
  }

  return (
    <div className="border-b-1 ml-[0px] flex h-[60px] w-full bg-white">
      {info && lessonSettingData && (
        <LessonSettingConfirmPopup
          item={info}
          settingInfo={lessonSettingData}
          showEnterPopup={showSettingPopup}
          setShowEnterPopup={setShowSettingPopup}
          handleAvatarClick={undefined}
          handleLessonStyleClick={undefined}
          inClass={false}
        />
      )}
      <PreparationNotYet
        showPrepPopup={showPrepPopup}
        setShowPrepPopup={setShowPrepPopup}
        handlePrepPopupClose={handlePrepPopupClose}
      />
      <PreparationAnswerNotSaved
        showSavePopup={showSavePopup}
        setShowSavePopup={setShowSavePopup}
        count={count}
        handleSaveButton={handleSaveButton}
      />
      <PreparationAnswerNotSaved
        showSavePopup={showSaveBeforeOverPopup}
        setShowSavePopup={setShowSaveBeforeOverPopup}
        count={count}
        handleSaveButton={handleSaveBeforeOverButton}
      />
      <div className="mx-[32px] flex w-full items-center justify-between">
        <div>
          {(disabled || notSaved) && ( // 수업 준비가 안끝나거나 답변이 다 저장되지 않은 경우
            <button
              onClick={() => {
                isReview ? goToReview() : handlePrepExit()
                sendClickEvent({ button_name: 'back' })
              }}
            >
              <div className="Body2-14Bd flex flex-row items-center">
                <QIcon_chevron_left className="h-[16px] w-[16px]" />
                <div className="">{t(n_common.btn_back_list)}</div>
              </div>
            </button>
          )}
          {!disabled && !notSaved && (
            <div
              className="cursor-pointer"
              onClick={() => {
                // router.push(urls.student.newPortal.lessons.upcomingLessons)
                router.back()
                sendClickEvent({ button_name: 'back' })
              }}
            >
              <div className="Body2-14Bd flex flex-row items-center">
                <QIcon_chevron_left className="h-[16px] w-[16px]" />
                <div className="">{t(n_common.btn_back_list)}</div>
              </div>
            </div>
          )}
        </div>
        <div className="ml-auto flex flex-row items-center">
          {isFreeTalkingOrWriting && (locale == LOCALE_KO || locale == LOCALE_EN) && (
            <div className="mr-[8px]">
              <GoogleTranslateBtn
                customBorderColor={COLORS.GRAY300}
                customTxtColor={COLORS.BLACK}
                customSize={BUTTON_SIZE.SM}
                showGoogleTranslate={showGoogleTranslator && showGoogleTranslator}
                setShowGoogleTranslate={setShowGoogleTranslator && setShowGoogleTranslator}
              />
            </div>
          )}
          <div className={classNames([isFreeTalkingOrWriting ? '' : 'mr-[8px]'])}>
            <DSButton
              onClick={onClickLessonSetting}
              size={BUTTON_SIZE.SM}
              type={BUTTON_TYPE.OUTLINE}
              customBorderColor={COLORS.GRAY300}
            >
              {loadSettingInfo ? (
                <div className="w-full">
                  <LoadingSpinner md show primary />
                </div>
              ) : (
                <div className="flex w-full space-x-[8px]">
                  <CreditBadge
                    duration={lesson?.duration ? lesson?.duration : ''}
                    style={{
                      paddingTop: '2px',
                      paddingBottom: '2px',
                      paddingLeft: '6px',
                      paddingRight: '6px',
                      color: COLORS.GRAY800,
                      backgroundColor: COLORS.GRAY100,
                      fontWeight: 'bold',
                    }}
                  />
                  <div className="flex cursor-pointer flex-row items-center justify-start">
                    <span className="text-[14px] font-medium text-black">{lesson?.formatted_start_date}</span>
                    <div className="mx-[8px] h-[16px] w-[1px] bg-gray-200"></div>
                    <span className="text-[14px] font-medium text-black">{lesson?.formatted_start_time_only}</span>
                  </div>
                </div>
              )}
            </DSButton>
          </div>
          {originalLesson && (
            <div className="hidden sm:flex">
              {!isFreeTalkingOrWriting && !isReview ? (
                <CompletePreparationTooltip
                  questionCnt={questionCnt}
                  tooltipContent={tooltipContent}
                  onClickButton={onClickButton}
                  showTooltip={showTooltip}
                  setShowTooltip={setShowTooltip}
                  disabled={disabled}
                />
              ) : (
                <></>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PreparationTab
